// Englische Fassung von ../de/mail.ts. Der Typ `MailKatalog` erzwingt die
// Parität Key für Key — ein fehlender Key ist TS2741, ein überzähliger TS2353.
//
// Bewusst NICHT übersetzt (Wortlaut identisch zur deutschen Fassung):
//   * Die TASTEN in `kuerzel.notizPalette` / `kuerzel.rechtsklickTaste`
//     („Strg+Umschalt+P", „Umschalt+F10"). Sie sind die BINDUNG und stehen
//     wortgleich in `mail/shortcuts.ts::keys`; eine übersetzte Taste wäre eine
//     Taste, die es nicht gibt.
//   * Das Datums-Muster `TT.MM.JJJJ` — die Eingabemaske ist deutsch (docs/i18n.md
//     §Nicht-Ziele), also muss die Beschriftung dasselbe Muster nennen.
//   * `abwesenheit.externerTextPlatzhalter` — der Beispieltext für die
//     Auslands-Abwesenheitsnotiz ist bereits englisch (er ist INHALT).
import type { MailKatalog } from "../de/mail";

export const mail: MailKatalog = {
  seite: {
    titel: "Mail",
    stand: (zeit) => `As of ${zeit}`,
    reiterPosteingang: "Inbox",
    reiterNachverfolgung: "Follow-up",
    reiterVorlagen: "Templates",
    reiterFragen: "Questions",
    abwesenheitsnotiz: "Out of office",
    neueMail: "New email",
    ansicht: "View",
    ansichtTitel: "View — how far the list is loaded",
    ansichtDichte: "Density",
    weitereBefehle: "More commands",
    tiefeAria: "Mail depth (how far inbox, sent and drafts are loaded)",
    tiefe: {
      kompakt: {
        label: "Compact",
        hinweis: "The last 25 messages from the past 14 days.",
      },
      erweitert: {
        label: "Extended",
        hinweis: "The last 50 messages from the past 30 days.",
      },
      maximal: {
        label: "Maximum",
        hinweis: "The last 100 messages from the past 90 days.",
      },
    },
    nichtErreichbarTitel: "Mail service unavailable",
    nichtErreichbarHinweis:
      "Your mailbox cannot be loaded right now. Try again in a moment.",
    nichtFreigeschaltetTitel: "Your mailbox is not enabled for Atlas yet",
    nichtFreigeschaltetHinweis:
      "As soon as email sync is switched on for you, your messages will appear here automatically.",
  },

  postfach: {
    wechseln: "Switch mailbox",
    eigenes: "My mailbox",
  },

  liste: {
    ungelesen: "Unread",
    an: "To:",
    keinEmpfaenger: "No recipient",
    unbekannt: "Unknown",
    anhang: "Attachment",
    inNachverfolgung: "Being followed up",
    archivieren: "Archive",
    aktionen: "Actions",
    aktionenTitel: "Actions (also via right-click)",
    keinBetreff: "(no subject)",
    wichtig: "Important",
    sfNichtErreichbar: "Salesforce unavailable",
    mehrLaden: "Load more",
    tiefeEntwuerfe: (n) =>
      `${n === 0 ? "No" : n} draft${n === 1 ? "" : "s"} loaded · no date limit`,
    tiefeNachrichten: (n, tage) =>
      `${n === 0 ? "No" : n} message${n === 1 ? "" : "s"} loaded · last ${tage} days`,
    leerOrdnerTitel: "This folder is empty",
    leerOrdnerHinweis: "There are no messages in this folder for the chosen period.",
    leerTrefferTitel: "No matches",
    leerTrefferHinweis:
      "There are no messages for your search. Adjust the search term or the filters.",
    leerPostfachTitel: "No messages",
    leerPostfachHinweis: "Your mailbox is empty for the chosen period.",
    keineMailGewaehltTitel: "No email selected",
    keineMailGewaehltHinweis:
      "Pick a message on the left to read it together with its Salesforce context.",
    keinEntwurfGewaehltTitel: "No draft selected",
    keinEntwurfGewaehltHinweis:
      "Pick a draft on the left to carry on writing it in the compose window.",
    menuAria: (betreff) => `Actions for “${betreff}”`,
    menuAllenAntworten: "Reply all",
    menuFreiAntworten: "Reply from scratch",
    menuWeiterleiten: "Forward",
    menuVorlage: "Insert template",
    menuTermin: "Meeting from this email",
    menuTerminTitel: (taste) => `Meeting from this email (${taste})`,
    menuNachverfolgen: "Mark for follow-up",
    menuAlsUngelesen: "Mark as unread",
    menuAlsGelesen: "Mark as read",
    menuArchivieren: "Archive",
    menuVerschieben: "Move to folder",
    menuLoeschenTitel: (taste) => `Delete (${taste})`,
  },

  suche: {
    platzhalter: (taste) => `Search the mailbox — Enter searches (${taste})`,
    titel: (tasten) =>
      `Search the mailbox: type a search term, then press Enter. Jump to the field with ${tasten}, clear it with Esc.`,
    aria: (tasten) =>
      `Search the mailbox — press Enter to search (jump to the field: ${tasten})`,
    laeuft: "Searching …",
    leeren: "Clear the search",
    leerenTitel: "Clear the search (Esc)",
    absender: "From (sender)",
    absenderAria: "Filter by sender address",
    filter: "Filter",
    filterTitel: "Filter — sender and period",
    filterAria: (n) => (n === 0 ? "Open filters" : `Open filters — ${n} active`),
    filterAktiv: (n) => `${n} filter${n === 1 ? "" : "s"} active`,
    filterZuruecksetzen: "Reset",
    schnellwahl: "Quick select",
    zeitraum: "Period",
    von: "from",
    bis: "to",
    vonAria: "Filter period from (TT.MM.JJJJ)",
    vonTitel: "Period from (TT.MM.JJJJ)",
    bisAria: "Filter period to (TT.MM.JJJJ)",
    bisTitel: "Period to (TT.MM.JJJJ)",
    tage7: "7 days",
    tage30: "30 days",
    datumBeideFalsch: "Please give from and to as TT.MM.JJJJ (e.g. 01.02.2026).",
    datumVonFalsch: "Please give from as TT.MM.JJJJ (e.g. 01.02.2026).",
    datumBisFalsch: "Please give to as TT.MM.JJJJ (e.g. 01.02.2026).",
    begriff: (q) => `»${q}«`,
    vonWem: (v) => `from ${v}`,
    abDatum: (d) => `since ${d}`,
    bisDatum: (d) => `until ${d}`,
    zeigtPosteingang: "the inbox",
    zeigtErgebnis: (frage) => `the result for ${frage}`,
    zeigtFilterErgebnis: (frage) => `the result of the filter search (${frage})`,
    veraltetLaeuft: (was) => `Searching … Below you still see ${was}.`,
    veraltet: (was) =>
      `Below you still see ${was} — not the result for what you typed. Press Enter to search.`,
    bereichGanzesPostfachFest:
      "Searched the whole mailbox — this folder cannot be searched on its own.",
    bereichGanzesPostfach: "Searched the whole mailbox.",
    bereichNurOrdner: (ordner) => `Search “${ordner}” only`,
    bereichInOrdner: (ordner) => `Searched in “${ordner}”.`,
    bereichWechselGanzesPostfach: "Search the whole mailbox",
    gedeckelt: (n) =>
      `Not complete: for this search Microsoft returns at most the ${n} most recent matches. Narrow the search term or the period to see older ones.`,
    gedeckeltEnde: (n) =>
      `End of the list — this is where Microsoft stops, not your search. For common terms Graph returns no more than ${n} matches.`,
    fehler:
      "The search could not be run. That is a fault of the application, not of your search term.",
  },

  ordner: {
    posteingang: "Inbox",
    gesendet: "Sent",
    entwuerfe: "Drafts",
    ordner: "Folders",
    weitereOrdner: "More folders",
    menuAria: "Folders in this mailbox",
    laedt: "Loading folders …",
    fehler: "The folders could not be loaded.",
    keine: "No folders available",
    aufklappen: (name) => `Expand ${name}`,
    zuklappen: (name) => `Collapse ${name}`,
    suchbereich: {
      inbox: "Inbox",
      archive: "Archive",
      sentitems: "Sent Items",
      drafts: "Drafts",
      deleteditems: "Deleted Items",
      junkemail: "Junk Email",
      outbox: "Outbox",
    },
    verschiebenTitel: "Move to folder",
    verschiebenBeschreibung:
      "The message will be moved into the chosen folder in Outlook.",
    filterPlatzhalter: "Filter folders …",
    filterAria: "Filter folders — Enter picks the only match",
    filterOhneTreffer: (begriff) => `No folder matches “${begriff}”.`,
    vorschlaegeTitel: "Most often filed in",
    vorschlagGrund: (anzahl, domain) => `${anzahl}× from ${domain}`,
    verschiebenFehlgeschlagen: "Moving failed",
    verschiebenFehlgeschlagenText: (ziel, grund) =>
      `The message is still where it was (target “${ziel}”). ${grund}`.trim(),
  },

  loeschen: {
    verschoben: "Moved to Deleted Items",
    rueckgaengig: "Undo",
    rueckgaengigFehlgeschlagen: "Could not put it back",
    rueckgaengigFehlgeschlagenText: (grund) =>
      `The message is still in Deleted Items. ${grund}`.trim(),
  },

  lesen: {
    zurueckZurListe: "Back to the list",
    nichtGefundenTitel: "Email not found",
    nichtGefundenHinweis: "This message no longer exists in the mailbox.",
    nichtErreichbarTitel: "Mail service unavailable",
    nichtErreichbarHinweis: "The message could not be loaded.",
    keinBetreff: "(no subject)",
    antwortVorschlagen: "Suggest a reply",
    allenAntworten: "Reply all",
    freiAntworten: "Reply from scratch",
    weiterleiten: "Forward",
    weitereAktionen: "More actions",
    kontext: "Context",
    kontextEinblenden: "Show the intelligence panel",
    alsUngelesenMarkieren: "Mark as unread",
    alsGelesenMarkieren: "Mark as read",
    zustandUngelesen: "Unread",
    zustandGelesen: "Read",
    anhang: "Attachment",
    wichtig: "Important",
    empfaengerAn: "To:",
    empfaengerKopie: "Cc:",
    weitereEmpfaenger: (n) => `+${n} more`,
    wenigerEmpfaenger: "fewer",
    archivieren: "Archive",
    verschieben: "Move to folder",
    zusammenfassung: "Summary",
    externeBilderAusgeblendet: "External images hidden",
    bildNichtVerfuegbar: "Image not available",
    keinInhalt: "(no content)",
    fruehereNachrichten: "Earlier messages inside this email",
    downloadFehlgeschlagen: "Download failed",
  },

  kontext: {
    reiterKontext: "Context",
    reiterFragen: "Questions",
    panelAusblenden: "Hide the panel",
    panelEinblenden: "Show context & questions",
    breiteZiehen: "Drag to resize",
    sfKontext: "Salesforce context",
    threadVerlauf: "Thread history",
    eingehend: "incoming",
    ausgehend: "outgoing",
    account: (name) => `Account: ${name}`,
    dringlichkeit: (wert) => `Urgency: ${wert}`,
    verknuepfteDatensaetze: "Linked records",
    missPending:
      "Salesforce context is unavailable right now — it will be tried again on the next refresh.",
    missIntern: "Internal email — no customer context in Salesforce.",
    missKeinTreffer: "There is no Salesforce record for this sender.",
    modulFehler: "Salesforce data for this module is unavailable right now.",
    linksDegradiert: "Linked records unavailable right now — Salesforce did not answer.",
    keineLinks: "No linked records found.",
    inSfOeffnen: (name) => `${name} — open in Salesforce`,
    konfidenz: "Match confidence of the link",
    gruppe: {
      Batch_Record: "Batch records",
      Registration: "Registrations",
      Sales_Position: "Sales positions",
      Artwork: "Artworks",
      Formulation_Group: "Formulation groups",
    },
    feld: {
      batch_no: "Batch",
      manufacturing_date: "Manufactured",
      expiry_date: "Expiry",
      country: "Country",
      type: "Type",
      launch_date: "Launch",
      quantity: "Quantity",
      value: "Value",
      expected_delivery_date: "Delivery date",
      phase: "Phase",
      valid_from: "Valid from",
    },
    stufe: {
      "Waiting for Customer FC": "Waiting for forecast",
      "Waiting for order": "Waiting for PO",
      "Approval process": "Approval process",
      "Receiving Confirmation from CMO": "CMO confirmation",
      "Under Production": "In production",
      Processing: "In progress",
      "Releasing goods": "Releasing goods",
    },
    account_: "Account",
    absender: "Sender",
    ohneZuordnung: "Not assigned",
    unbekannterAbsender: "Unknown sender",
    lead: "Lead",
    funktionNichtHinterlegt: "No role on record",
    keinAccount: "No account linked in Salesforce — no key figures.",
    accountFehlerReferenz: "Invalid account reference.",
    accountFehlerLaden: "The account context could not be loaded.",
    erneut: "Retry",
    keineModule: "No modules selected.",
    keineDaten: "No data.",
    keineUmsatzdaten: "No revenue data.",
    gesamt: "Total:",
    inklAuftragsbestand: (jahr) => `incl. order backlog (from ${jahr})`,
    istUmsatz: "Actual revenue",
    balkenTitel: (jahr, umsatz, auftraege) =>
      `${jahr}: ${umsatz} · ${auftraege} orders`,
    keineOffenenBestellungen: "No open orders.",
    keineAuftraegeAbwicklung: "No orders in progress.",
    keineRegistrierungen: "No registrations in progress.",
    wenigerAnzeigen: "Show fewer",
    alleAnzeigen: (n) => `Show all ${n}`,
    launch: "Launch",
    launchTitel: (datum) => `Launch: ${datum}`,
    modul: {
      revenue_by_year: {
        label: "Revenue trend",
        beschreibung: "Revenue per year including the order backlog.",
      },
      open_orders: {
        label: "Open orders (waiting for customer)",
        beschreibung: "Orders waiting for a forecast or a purchase order.",
      },
      processing_orders: {
        label: "In progress",
        beschreibung: "Orders in production, approval or processing.",
      },
      registrations: {
        label: "Registrations in progress",
        beschreibung: "Ongoing approvals per country.",
      },
    },
    panelKonfigurieren: "Configure the panel",
    panelKonfigurierenBeschreibung:
      "Which modules are shown in the account context.",
    panelStandardHinweisA: "Without your own selection the ",
    panelStandardHinweisB: "default of your department",
    panelStandardHinweisC: " applies.",
    panelGespeichert: "Panel configuration saved",
    panelNichtGespeichert: "Could not be saved on the server",
  },

  composer: {
    kopfEntwurf: "Draft",
    kopfNeueMail: "New email",
    kopfAllenAntworten: "Reply all",
    kopfWeiterleiten: "Forward",
    kopfKiVorschlaege: "AI reply suggestions",
    kopfFreiAntworten: "Reply from scratch",
    vorschlaegeLaden: "Creating suggestions … (up to 20 s)",
    fehlerDienst: "The mail service is unavailable right now.",
    fehlerGenerierung: "Generating the reply failed.",
    fehlerAllgemein: "The suggestions could not be created.",
    varianteBearbeiten: "Edit this variant",
    zurueckZuVarianten: "Back to the variants",
    vorlagen: (n) => `Templates: ${n}`,
    ectoinWissen: "Ectoin knowledge",
    ectoinQuelle: "Ectoin source",
    an: "To",
    anAlle: "To everyone (editable)",
    anPlatzhalter: "recipient@example.com",
    cc: "Cc",
    ccHinweis: "optional, separate several with a comma",
    optional: "optional",
    bcc: "Bcc",
    bccHinweis: "optional — recipients cannot see each other",
    bccHinzufuegen: "Add Bcc",
    betreff: "Subject",
    nachricht: "Message",
    nachrichtPlatzhalter: "Write your message …",
    notiz: "Note",
    notizOptional: "Note (optional)",
    notizPlatzhalter: "Optional note for the forward …",
    signatur: (name) => `Signature${name ? ` (${name})` : ""} — `,
    signaturSchonImText: "is already in your text and will NOT be appended again",
    signaturWirdAngehaengt: "will be appended when you send",
    signaturAbgeschaltet: "switched off for this email — will NOT be appended",
    signaturSchalterAria: "Append the signature to this email",
    signaturSchalterTitel:
      "Applies to this email only. The next compose window appends it again.",
    ausDeinemPostfach: "Sent straight from your mailbox.",
    entwurfSpeichert: "Saving the draft …",
    entwurfGespeichert: (zeit) => `Draft saved · ${zeit}`,
    wirdGesendet: "Sending …",
    rueckgaengig: (sekunden) => `Undo (${sekunden})`,
    senden: "Send",
    rueckholzeit: "Undo window",
    rueckholzeitAria: "Undo window after sending",
    rueckholzeitOption: (sekunden) =>
      sekunden === 0 ? "Send immediately" : `${sekunden} sec. undo window`,
    keinEmpfaenger: "No recipient",
    keinEmpfaengerText: "Please give at least one recipient address.",
    weitergeleitet: "Forwarded",
    gesendet: "Sent",
    gesendetAn: (empfaenger) => `From your mailbox to ${empfaenger}.`,
    gesendetAnVerteiler: "From your mailbox to the distribution list.",
    sendenFehlgeschlagen: "Sending failed",
    sendenFehlerAnhangSync:
      "The attachments of the draft could not be updated — nothing was sent. Please try again.",
    sendenFehler403: "Your mailbox is not (yet) allowed to send.",
    sendenFehler413:
      "The attachments are too large together (max. around 2.5 MB per email). Please remove a file or share it as a link.",
    sendenFehler404: "The referenced message is no longer in the mailbox.",
    sendenFehler502: "The mail service could not send the message.",
    sendenFehlerSonst: "Please try again later.",
    fett: "Bold",
    fettTitel: "Bold (Ctrl+B)",
    kursiv: "Italic",
    kursivTitel: "Italic (Ctrl+I)",
    aufzaehlung: "Bulleted list",
    linkEinfuegen: "Insert a link",
    linkEinfuegenTitel: "Insert a link (Ctrl+K)",
    linkOk: "OK",
    assistHinweisA: "AI: ",
    assistHinweisB: " keep writing · ",
    assistHinweisC: " paragraph",
    assistHinweisText: "Text",
    assistHinweisAnweisung: "<instruction>>>",
    assistLaeuftGefuehrt: "The writing aid is drafting the paragraph …",
    assistLaeuftFrei: "The writing aid is carrying on …",
    assistDauer: "This takes a few seconds.",
    assistFertig: "Writing aid inserted.",
    assistGebremst: (zeichen) => `Repetition removed (${zeichen} characters).`,
    assistFehlerTitel: "The writing aid did not work.",
    assistFehlerNachsatz: "Your instruction is still in the draft.",
    assistNochmal: "Again",
    assistGrund502: "The model did not return any usable text.",
    assistGrund503: "The mail service is unavailable right now.",
    assistGrund403: "This mailbox is not enabled.",
    assistGrund404: "The referenced message no longer exists.",
    assistGrundOffline: "No connection to the server.",
    assistGrundStatus: (status) => `The server answered with ${status}.`,
    assistGrundUnbekannt: "Unknown error.",
    assistLeer: "The model returned nothing.",
    assistNurWiederholt: "The model only repeated what is already there.",
    assistStelleWeg: "The spot in the draft can no longer be found.",
  },

  anhaenge: {
    ablegen: "Drop the file here to attach it",
    dateiAnhaengen: "Attach a file",
    anhang: "Attachment",
    entfernen: "Remove the attachment",
    leseFehler: "Attaching failed",
    leseFehlerText: (name) => `“${name}” could not be read.`,
    zuGross: (dateien) =>
      `${dateien.map((n) => `“${n}”`).join(", ")} ${
        dateien.length > 1 ? "are" : "is"
      } too large and ${dateien.length > 1 ? "were" : "was"} not attached. ` +
      "All attachments of one email must not exceed around 2.5 MB together — " +
      "please share larger files as a link.",
    entwurfNichtAngelegt:
      "The draft could not be created — the attachments are not buffered yet. They will still go out when you send.",
    syncZuGross:
      "The attachments are too large for the draft together (max. around 2.5 MB per email). Please remove a file.",
    syncFehler:
      "The attachment could not be buffered in the draft. It stays here and goes out when you send.",
    entfernenFehler:
      "The attachment could not be removed from the buffered draft. It will be removed again before sending.",
    seiten: (n) => `${n} pages`,
    ansehen: (name) => `View ${name}`,
    keineVorschauTitel: (name) =>
      `${name} — there is no preview for this format, only the download`,
    herunterladenTitel: (name) => `Download ${name}`,
    anlageHerunterladen: "Download the attachment",
    alleHerunterladen: (n) => `Download all (${n})`,
    alleHerunterladenTitel: (n) =>
      `Download all ${n} attachments as separate files (no ZIP)`,
    alleHerunterladenLaeuft: "Loading the attachments …",
    alleFehlgeschlagen: "Could not load the attachments",
    alleFehlgeschlagenText:
      "The attachments cannot be loaded in one go right now. One by one still works.",
    alleZuGross:
      "Together the attachments are too large for one download. Please download them one by one.",
    vorschauSchliessen: "Close the preview",
    vorschauTitel: (name) => `Preview ${name}`,
    laedt: "Loading the attachment …",
    fehler404: "This attachment can no longer be retrieved.",
    fehler403: "This mailbox has not been shared with you.",
    fehlerStatus: (status) => `The attachment could not be loaded (error ${status}).`,
    fehlerAllgemein: "The attachment could not be loaded.",
    keineVorschau: "There is no preview for this format.",
  },

  entwuerfe: {
    entwurf: "Draft",
    keinEmpfaenger: "(no recipient)",
    keinBetreff: "(No subject)",
    enthaeltAnhaenge: "Has attachments",
    loeschen: "Delete the draft",
    loeschenFehlgeschlagen: "Deleting failed",
    leerTitel: "No drafts",
    leerHinweis:
      "As soon as you start writing an email it is saved here as a draft.",
  },

  vorlagen: {
    einfuegen: "Insert template",
    wirdAufgeloest: "Resolving …",
    laedt: "loading …",
    keine: "No templates.",
    datensatzWaehlen: (name) => `“${name}” — pick a record`,
    ohneDatensatz: "Without a record",
    aufloesenFehlgeschlagen: "Resolving failed",
    aufloesenNichtGefunden: "Template not found.",
    aufloesenErneut: "Please try again.",
    eingefuegt: (name) => `Template “${name}” inserted`,
    felderGefuellt: (n) => `${n} fields filled`,
    offen: (n) => `${n} open`,
    anhang: (n) => `${n} attachment`,
    sfNichtErreichbar: "SF unavailable",
    reiterHinweis: "Department templates as the basis for AI replies.",
    neu: "New",
    ladefehlerTitel: "The templates could not be loaded",
    ladefehlerHinweis: "Try again in a moment.",
    leerTitel: "No templates yet",
    leerHinweis:
      "Create your first department template — it then feeds the AI replies as a building block.",
    leerAktion: "Create a template",
    seed: "Seed",
    manuell: "Manual",
    deaktivieren: "Deactivate",
    wirklichDeaktivieren: "Really deactivate?",
    deaktiviert: "Template deactivated",
    deaktivierenFehlgeschlagen: "Deactivating failed",
    modalBearbeiten: "Edit template",
    modalNeu: "New template",
    modalBeschreibung: "Department template as a building block for AI replies.",
    gespeichert: "Template saved",
    angelegt: "Template created",
    kategorie: "Category",
    kategorieHinweis: "e.g. Quality, Sales, Regulatory",
    kategoriePlatzhalter: "Quality",
    subkategorie: "Subcategory",
    subkategoriePlatzhalter: "Amendment",
    name: "Name",
    namePlatzhalter: "Amendment acknowledgement",
    text: "Text",
    textHinweis: "Markdown allowed; [Name] as the signature placeholder.",
    textPlatzhalter: "Dear Sir or Madam, …",
    notizen: "Notes",
    notizenHinweis: "When this template fits (internal only).",
  },

  fragen: {
    beta: "Beta",
    betaHinweis: " — the query engine is still being validated; please check the results.",
    leerTitel: "Ask Salesforce in plain language",
    leerHinweis:
      "Ask a question about accounts, revenue or registrations — the answer comes back as a table.",
    beispiele: [
      "How many accounts are there?",
      "Revenue of Opella 2025",
      "Open registrations for Poland",
    ],
    platzhalter: "Ask Salesforce … (e.g. revenue of Opella 2025)",
    fragen: "Ask",
    denktNach: "Salesforce is thinking …",
    keineTreffer: "No matches.",
    treffer: (n) => `${n} matches`,
    nurErste50: "only the first 50 shown",
    details: "Details",
    objekt: (name) => `Object: ${name}`,
    konfidenz: (prozent) => `Confidence: ${prozent}%`,
    soqlKopiert: "SOQL copied",
    soqlKopierenFehler: "Copying is not possible",
    soqlKopierenAria: "Copy the SOQL",
    kopieren: "Copy",
    wert: "Value",
    wertN: (n) => `Value ${n}`,
    zeitueberschreitung: "Timeout — the query took too long.",
    engineWeg: "The query engine is unavailable right now.",
    nichtBeantwortbar: "The question cannot be answered.",
    konkreter:
      "Put the question more concretely (object, period, country) and try again.",
    stattdessenFragen: (frage) => `Ask this instead: ${frage}`,
    abstainUnanswerable: "This question cannot be answered from the Salesforce data.",
    abstainKeinObjekt:
      "I could not map the question to a data object. Put it more concretely (object, period, country).",
    abstainUngueltig:
      "The generated query was not valid. Please put the question differently.",
    abstainTimeout: "The query engine took too long. Please try again.",
    abstainSonst: "I cannot give a reliable answer to that.",
  },

  nachfass: {
    reiterHinweis:
      "Marked emails follow up automatically: friendly → firmer → plain, then a Teams escalation meeting. Adjust or pause them here.",
    ladefehlerTitel: "The follow-ups could not be loaded",
    ladefehlerHinweis: "Try again in a moment.",
    leerTitel: "No follow-ups yet",
    leerHinweis:
      "Mark an email in the inbox with the alarm-clock icon — it then runs automatically straight away.",
    keinBetreff: "(no subject)",
    unbekannt: "Unknown",
    zustand: {
      draft: "Draft",
      armed: "live",
      paused: "paused",
      answered: "answered",
      stopped: "stopped",
      needs_decision: "Decision needed",
    },
    antwort: {
      none: "no answer",
      holding: "holding",
      real: "real answer",
      ooo: "out of office",
    },
    entscheidungZaehler: (n) => `${n} items need a decision`,
    entscheidungTitel: "Needs your decision",
    entscheidungHinweis:
      "These items received an answer that was neither an out-of-office nor a meeting confirmation — or the follow-up steps are used up and Atlas is asking whether an escalation meeting should be set up. They are on hold: nothing goes out and no meeting is created until you have decided.",
    grundEchteAntwort:
      "A substantive answer came in — done, or keep at it?",
    grundUnzuordenbar: "An email in this thread could not be mapped — please take a look.",
    grundDatumUnklar: "The answer names a date that could not be read reliably.",
    grundEskalation:
      "The follow-up steps are used up. Should Atlas now set up an escalation meeting in your name and send the escalation email?",
    grundSonst: "An answer came in that needs a decision.",
    terminAnlegen: "Create the meeting",
    erledigt: "Done",
    keineEskalation: "No escalation",
    weiterNachfassen: "Keep following up",
    inMailOeffnen: "Open in Mail",
    vorgeschlagenerTermin: "Proposed slot",
    eingeladen: (n) => `Invited (${n})`,
    antwortUnbekannt: "(unknown)",
    antwortWeg:
      "Email can no longer be found — it was deleted for good. The assessment and reasoning above remain.",
    antwortFehler: "The email could not be loaded.",
    laeuftPausieren: "Running – pause",
    pausiertFortsetzen: "Paused – resume",
    erstScharfschalten: "Go live first",
    laeuftKlickPausieren: "Running – click to pause",
    pausiertKlickFortsetzen: "Paused – click to resume",
    naechsterSchrittEskalation: "Next step: escalation",
    naechsteNachfrage: "Next follow-up",
    eskalationPraefix: "Escalation ",
    naechstePraefix: "next ",
    spracheHaus:
      "Language of the follow-up emails — German, because the distribution list only holds bitop addresses",
    spracheErkannt: "Language of the follow-up emails — detected from the conversation",
    empfaengerFehlt: "Recipient (To) is missing — add it in the adjust dialog",
    internesGegenueber:
      "The counterpart is bitop-internal — it only starts after you confirm",
    scharf: "Go live",
    anpassen: "Adjust",
    scharfgeschaltet: "Live",
    scharfgeschaltetText:
      "Schedule calculated — reminders & escalation now run automatically.",
    scharfschaltenFehler: "Going live is not possible",
    gestoppt: "Stopped",
    reaktiviert: "Reactivated",
    aktionFehlgeschlagen: "The action failed",
    loeschenTitel: "Delete this follow-up?",
    loeschenBeschreibung: (betreff) =>
      `“${betreff}” will be removed together with its schedule.`,
    geloescht: "Deleted",
    loeschenFehlgeschlagen: "Deleting failed",
    entscheidungFehlgeschlagen: "The decision failed",
    meldungDoneTitel: "Filed as done",
    meldungDoneText: "The item no longer appears in the decision list.",
    meldungContinueTitel: "The follow-up carries on",
    meldungContinueText: "The schedule restarts from today.",
    meldungEscalateTitel: "Escalation meeting created",
    meldungEscalateText: (termin) =>
      `${termin} — the invitation and the escalation email are out.`,
    meldungEscalateFallback: "The meeting",
    meldungNoEscalateTitel: "No escalation",
    meldungNoEscalateText:
      "No meeting was created and no email was sent. The item is stopped.",
    markiertLaeuftTitel: "The follow-up is running",
    markiertLaeuftText: (datum) =>
      `First follow-up on ${datum} · adjustable in the Follow-up tab.`,
    markiertLaeuftOhneDatum: "Runs automatically · adjustable in the Follow-up tab.",
    markiertTitel: "Marked for follow-up",
    markiertInternText:
      "The counterpart is bitop-internal — check it in the Follow-up tab and take it live.",
    markiertOffenText:
      "Recipient is missing — add it in the Follow-up tab and take it live.",
    markierenFehlgeschlagen: "Marking failed",
    abwartendSeit: (datum) => `holding since ${datum}`,
    chipZusage: "Promised date",
    chipZusageTitel: (datum) =>
      `The other side committed to ${datum} — the schedule snapped to it.`,
    eingerastet: (ziel, quelle) =>
      `snapped to ${ziel} — promised in the reply of ${quelle}`,
    eingerastetOhneQuelle: (ziel) => `snapped to ${ziel} — promised in the reply`,
    bestaetigungVerschickt: (datum) => `confirmation sent in your name on ${datum}`,
    bestaetigungStattNachfrage: (datum) =>
      `confirmation sent in your name on ${datum} — the follow-up that was due fell away`,
    nachfrageUebersprungen: (datum) =>
      `follow-up due ${datum} skipped — the confirmation superseded it`,
    uebersprungenGrund: " · skipped: the confirmation superseded this follow-up",
    nachfassZaehler: (n) => `${n} items need your attention`,
    fahrplanStart: "Start",
    fahrplanEskalation: "Esc.",
    fahrplanReminder: (n) => `R${n}`,
    konfigurationGespeichert: "Configuration saved",
    nichtGespeichert: "Not saved",
    unbekannterFehler: "Unknown error",
    feldAbgelehnt: (wert) => `“${wert}” was rejected`,
    feldLabel: {
      to: "To (recipients)",
      cc: "CC",
      bitop_attendees: "bitop attendees",
      partner_attendees: "Partner attendees",
    },
    betreffPraefix: (betreff) => `Subject: ${betreff}`,
    nochNichtScharf: "Not live yet — anchor = today (preview).",
    eskaliert: (gesendet, gesamt) =>
      `Escalated · ${gesendet}/${gesamt} follow-ups sent.`,
    nachfragenGesendet: (gesendet, gesamt) =>
      `${gesendet}/${gesamt} follow-ups sent`,
    naechsteAm: (was, datum) => ` · ${was} on ${datum}`,
    wortEskalation: "escalation",
    wortNaechste: "next one",
    tonLadder: ". Tone: friendly → firmer → plain, then a meeting.",
    intervall: "Interval (days)",
    intervallHinweis: "Gap between the follow-ups.",
    anzahlNachfragen: "Number of follow-ups",
    anzahlNachfragenHinweis: "Follow-ups before the escalation.",
    neuerFahrplan: "New schedule after saving",
    an: "To (recipients)",
    anPlatzhalter: "Type a name or mail@customer.com",
    cc: "CC",
    ccPlatzhalter: "Type a name or cc@…",
    verteilerAlle: "All follow-ups to the whole distribution list",
    verteilerAlleAn:
      "Reminders + escalation go to To + CC (like the original email).",
    verteilerAlleAus:
      "Gentle reminders to the main recipient only; the escalation goes to everyone.",
    eskalationsMeeting: "Escalation meeting (Teams) · ",
    eingeladenAnzahl: (n) => `${n} invited`,
    eskalationHinweis:
      "You organise it; the whole distribution list is invited automatically — remove individuals with a click (you can add them back). Affects only the meeting, not the follow-up emails.",
    keinVerteiler:
      "No distribution list recorded — on escalation only the email goes out.",
    wiederEinladen: "Invite again",
    ausEinladungEntfernen: "Remove from the invitation",
    bitopTeilnehmer: "bitop attendees",
    bitopTeilnehmerPlatzhalter: "Type a name or person@bitop.de",
    partnerTeilnehmer: "Partner attendees",
    partnerTeilnehmerPlatzhalter: "Type a name or person@partner.com",
    notiz: "Note",
    notizPlatzhalter: "Context / agenda for the escalation meeting …",
    optional: "optional",
    chipEntfernen: (adresse) => `Remove ${adresse}`,
    chipAria: (feld) => `${feld} — type a name or email address`,
    chipHinweisA: "↑↓",
    chipHinweisB: " to select · ",
    chipHinweisC: "Enter",
    chipHinweisD: " confirms",
    chipSucht: "Searching …",
    chipKeinVorschlag:
      "No suggestion — type the full address, Enter accepts it.",
    extern: "External",
  },

  termin: {
    ueberschrift: "Meeting invitation — reply to the organiser",
    zusagen: "Accept",
    mitVorbehalt: "Tentative",
    absagen: "Decline",
    antwortVerschickt: "Reply sent",
    antwortFehlgeschlagen: "The reply failed",
    fehlerDienst: "Calendar service unavailable.",
    fehlerFreigabe: "This calendar has not been shared with you.",
    status: (wert) => `Status: ${wert}`,
    label: {
      accept: "Acceptance sent",
      tentativelyAccept: "Accepted tentatively",
      decline: "Decline sent",
    },
  },

  abwesenheit: {
    titel: "Out-of-office reply",
    laedt: "Loading …",
    nichtFreigeschaltet:
      "This mailbox is not enabled. The out-of-office reply is only available for synchronised mailboxes.",
    nichtVerfuegbar:
      "The out-of-office reply is currently unavailable (mailbox settings not enabled yet). Saving is disabled.",
    automatischeAntwort: "Automatic reply",
    aktiv: "on — incoming emails are answered automatically",
    aus: "off",
    aktivierenAria: "Switch the out-of-office reply on",
    internerText: "Internal reply text",
    internerTextHinweis: "Goes to senders inside the organisation.",
    internerTextPlatzhalter: "I am currently out of the office …",
    zeitraumFestlegen: "Set a period",
    zeitraumAktiv: "only active between From and To",
    zeitraumInaktiv: "active until switched off manually",
    zeitraumUnvollstaendigA: "Period incomplete — ",
    zeitraumBeideFehlen: "From and To are missing",
    zeitraumVonFehlt: "the From date is missing",
    zeitraumBisFehlt: "the To date is missing",
    zeitraumUnvollstaendigB:
      ". Fill in the dates or switch “Set a period” off; without a period the out-of-office reply cannot be limited.",
    vonDatum: "From (date)",
    vonUhrzeit: "From (time)",
    bisDatum: "To (date)",
    bisUhrzeit: "To (time)",
    datumFormat: "Please use TT.MM.JJJJ",
    datumFehlt: "Date missing",
    vonDatumAria: "Away from, date (TT.MM.JJJJ)",
    vonUhrzeitAria: "Away from, time",
    bisDatumAria: "Away until, date (TT.MM.JJJJ)",
    bisUhrzeitAria: "Away until, time",
    externeEmpfaenger: "Reply to recipients outside the organisation",
    externKeine: "No external replies",
    externKontakte: "My contacts only",
    externAlle: "All external senders",
    externerText: "External reply text",
    externerTextHinweis: "Goes to senders outside the organisation.",
    externerTextPlatzhalter: "I am currently out of office …",
    aktiviert: "Out-of-office reply switched on",
    deaktiviert: "Out-of-office reply switched off",
    fehler403:
      "Mailbox not enabled — the out-of-office reply cannot be set.",
    fehlerSonst:
      "Saving failed (mailbox settings currently unavailable). Please try again later.",
  },

  kuerzel: {
    titel: "Keyboard shortcuts",
    beschreibung: "Outlook-native — modelled on Office, no Gmail shortcut.",
    aktion: {
      delete: "Delete the message",
      space: "Page on — at the end jump to the next unread email",
      palette: "Open the command palette",
      search: "Focus the search",
      termin: "Meeting from this email",
      help: "Keyboard shortcut overview",
    },
    notizPalette: "in the compose editor: Strg+Umschalt+P",
    hinweisSchliessen: "Dismiss the hint",
    erstHinweisNeu: "New: ",
    erstHinweisPalette: " = command palette · ",
    erstHinweisUebersicht: " = all keyboard shortcuts",
    rechtsklickNeu: "New: ",
    rechtsklickWort: "Right-click",
    rechtsklickText:
      " on an email opens every action — move, follow up, reply. With the keyboard: ",
    rechtsklickTaste: "Umschalt+F10",
  },

  benachrichtigung: {
    nichtVerfuegbar: "Notifications unavailable",
    nichtVerfuegbarText: "This browser does not support web push notifications.",
    aktiv: "Notifications on",
    aktivText: "You will now get a hint for new, matched emails.",
    aktivierungFehlgeschlagen: "Switching on failed",
    bitteErneut: "Please try again.",
    imBrowserDeaktiviert: "Notifications disabled in the browser",
    imBrowserDeaktiviertTitel:
      "Allow them again in the browser settings to receive notifications.",
    aktivieren: "Switch notifications on",
  },

  kontakte: {
    herkunft: {
      signature: "from the signature",
      account: "from the account",
      sender: "from the sender",
    },
    geaendert: "changed",
    erforderlich: "required",
    leer: "empty",
    mobilE164Hinweis:
      "The mobile number must be in international E.164 format: + country code, then the number, without spaces or brackets (e.g. +491701234567). The national leading zero is dropped.",
    kontaktEinstieg: "This sender is not a contact in Salesforce yet.",
    kontaktAnlegen: "Create contact",
    kontaktModalTitel: "Create a contact in Salesforce",
    kontaktModalBeschreibung: (account) => `Will be created under “${account}”`,
    kontaktAngelegt: (name) => `${name} was created in Salesforce.`,
    kontaktFallbackName: "Contact",
    kontaktAccount: (account) => `Account: ${account}`,
    kontaktNichtGespiegelt:
      "The contact has been created but only appears in Atlas after the next sync.",
    kontaktVorschlagFehler502:
      "The proposal could not be generated. Please try again.",
    kontaktVorschlagFehler: "The proposal could not be loaded.",
    kontaktAnlegenFehler: "The contact could not be created.",
    kontaktOhneNachname: "Without a last name Salesforce cannot create the contact.",
    pruefenHinweis:
      "Please check and correct where needed. Empty fields stay empty — nothing is guessed.",
    leadBekannt: "This address is already recorded in Salesforce as a lead.",
    leadEinstieg: "There is no Salesforce account for this sender.",
    leadAnlegen: "Create as lead",
    leadModalTitel: "Create a lead in Salesforce",
    leadModalBeschreibung: (email) => `For ${email}`,
    leadKnopf: "Create lead",
    leadAngelegt: (name) => `${name} was created in Salesforce.`,
    leadFallbackName: "The lead",
    leadVorschlagFehler: "The proposal could not be loaded.",
    leadAnlegenFehler: "The lead could not be created.",
    leadOhneNachname: "Without a last name Salesforce cannot create the lead.",
    leadPruefenHinweis:
      "Please check and correct where needed. Empty fields stay empty — nothing is guessed. Salesforce sets the email address itself from the message header.",
    rechercheLaeuft: "Research running … (takes about 25 seconds)",
    rechercheFertig: (n) =>
      `Research finished, ${n} ${n === 1 ? "field" : "fields"} filled.`,
    rechercheFehler:
      "The research failed. The lead has been created and can be completed by hand in Salesforce.",
    rechercheOffen:
      "The lead has been created. The research is still running — its result will appear in Salesforce later.",
  },
};
